import { useState, useEffect } from 'react';
import { templatesApi } from '../../api/client';
import { useEditorStore } from '../../store/editorStore';
import toast from 'react-hot-toast';
import PageTemplatesPanel from './PageTemplatesPanel';
import ConfirmDialog from '../ConfirmDialog';

const CATEGORIES = [
  'General',
  'Marketing',
  'Fashion',
  'Travel',
  'Food',
  'Tech',
  'Education',
  'Real Estate',
  'E-commerce',
  'Health',
  'Finance',
  'Lifestyle',
  'Entertainment',
];

export default function TemplatesPanel() {
  const story = useEditorStore((s) => s.story);
  const isDirty = useEditorStore((s) => s.isDirty);
  const saveStory = useEditorStore((s) => s.saveStory);

  // Save-as-template form state
  const [showSaveForm, setShowSaveForm] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saveDescription, setSaveDescription] = useState('');
  const [saveCategory, setSaveCategory] = useState('General');
  const [saveTags, setSaveTags] = useState('');
  const [saving, setSaving] = useState(false);

  // Templates list state
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmTemplate, setConfirmTemplate] = useState(null);

  const fetchTemplates = () => {
    setLoading(true);
    templatesApi
      .list()
      .then(setTemplates)
      .catch(() => toast.error('Failed to load templates'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const myTemplates = templates.filter((t) => !t.isBuiltIn);
  const builtInTemplates = templates.filter((t) => t.isBuiltIn);

  const handleSave = async () => {
    if (!story) return;
    if (!saveName.trim()) {
      toast.error('Template name is required');
      return;
    }
    setSaving(true);
    try {
      if (isDirty) {
        await saveStory();
        const { saveError } = useEditorStore.getState();
        if (saveError) {
          toast.error('Failed to save story before creating template');
          setSaving(false);
          return;
        }
      }
      const tags = saveTags
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean);
      await templatesApi.saveFromStory({
        storyId: story.id,
        name: saveName.trim(),
        description: saveDescription.trim() || undefined,
        category: saveCategory,
        tags: tags.length > 0 ? tags : undefined,
      });
      toast.success('Template saved!');
      setShowSaveForm(false);
      setSaveName('');
      setSaveDescription('');
      setSaveCategory('General');
      setSaveTags('');
      fetchTemplates();
    } catch (err) {
      toast.error(err.message || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleUseTemplate = async (template) => {
    try {
      const newStory = await templatesApi.createStory(template.id, {
        title: template.name,
      });
      window.location.href = `/marketing/story-builder/${newStory.id}/edit`;
    } catch (err) {
      toast.error(err.message || 'Failed to create story from template');
    }
  };

  const handleDelete = (template) => {
    setConfirmTemplate(template);
  };

  const doDelete = async () => {
    try {
      await templatesApi.delete(confirmTemplate.id);
      toast.success('Template deleted');
      fetchTemplates();
    } catch (err) {
      toast.error(err.message || 'Failed to delete template');
    } finally {
      setConfirmTemplate(null);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '6px 8px',
    borderRadius: 6,
    border: '1px solid var(--t-border)',
    background: 'var(--t-surface)',
    color: 'var(--t-text)',
    fontSize: 12,
    outline: 'none',
  };

  return (
    <div className="p-2" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* ── Section A: Save as Template ── */}
      <div>
        <h3 className="panel-label mb-1">Save as Template</h3>
        {!showSaveForm ? (
          <button
            onClick={() => setShowSaveForm(true)}
            disabled={!story}
            style={{
              width: '100%',
              padding: '8px 12px',
              borderRadius: 6,
              border: 'none',
              background: 'var(--brand-primary)',
              color: '#fff',
              fontSize: 12,
              fontWeight: 600,
              cursor: story ? 'pointer' : 'not-allowed',
              opacity: story ? 1 : 0.5,
            }}
          >
            Save Current Story as Template
          </button>
        ) : (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              padding: 10,
              borderRadius: 8,
              border: '1px solid var(--t-border)',
              background: 'var(--t-panel)',
            }}
          >
            <input
              placeholder="Template name *"
              value={saveName}
              onChange={(e) => setSaveName(e.target.value)}
              style={inputStyle}
            />
            <textarea
              placeholder="Description (optional)"
              value={saveDescription}
              onChange={(e) => setSaveDescription(e.target.value)}
              rows={2}
              style={{ ...inputStyle, resize: 'vertical' }}
            />
            <select
              value={saveCategory}
              onChange={(e) => setSaveCategory(e.target.value)}
              style={inputStyle}
            >
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
            <input
              placeholder="Tags (comma separated)"
              value={saveTags}
              onChange={(e) => setSaveTags(e.target.value)}
              style={inputStyle}
            />
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                onClick={handleSave}
                disabled={saving}
                style={{
                  flex: 1,
                  padding: '6px 10px',
                  borderRadius: 6,
                  border: 'none',
                  background: 'var(--brand-primary)',
                  color: '#fff',
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                  opacity: saving ? 0.6 : 1,
                }}
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
              <button
                onClick={() => setShowSaveForm(false)}
                style={{
                  padding: '6px 10px',
                  borderRadius: 6,
                  border: '1px solid var(--t-border)',
                  background: 'transparent',
                  color: 'var(--t-muted)',
                  fontSize: 12,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Section B: My Templates / Built-in Templates ── */}
      <div>
        <h3 className="panel-label mb-1">My Templates</h3>
        {loading ? (
          <p className="text-xs" style={{ color: 'var(--t-muted)' }}>
            Loading templates...
          </p>
        ) : myTemplates.length === 0 ? (
          <p className="text-xs" style={{ color: 'var(--t-muted)' }}>
            No custom templates yet. Save a story as a template to get started.
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {myTemplates.map((t) => (
              <TemplateCard
                key={t.id}
                template={t}
                onUse={handleUseTemplate}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}

        {builtInTemplates.length > 0 && (
          <>
            <h3 className="panel-label mb-1" style={{ marginTop: 12 }}>
              Built-in Templates
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {builtInTemplates.map((t) => (
                <TemplateCard
                  key={t.id}
                  template={t}
                  onUse={handleUseTemplate}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {/* ── Section C: Page Templates (existing) ── */}
      <div style={{ borderTop: '1px solid var(--t-border)', paddingTop: 12 }}>
        <PageTemplatesPanel />
      </div>

      <ConfirmDialog
        isOpen={confirmTemplate !== null}
        onClose={() => setConfirmTemplate(null)}
        onConfirm={doDelete}
        title="Delete template?"
        message={confirmTemplate ? `"${confirmTemplate.name}" will be permanently deleted.` : ''}
        confirmLabel="Delete"
      />
    </div>
  );
}

function TemplateCard({ template, onUse, onDelete }) {
  const pageCount = template.pages?.length ?? 0;
  const createdDate = new Date(template.createdAt).toLocaleDateString();

  return (
    <div
      style={{
        padding: '8px 10px',
        borderRadius: 8,
        border: '1px solid var(--t-border)',
        background: 'var(--t-surface)',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p
            className="text-xs font-semibold truncate"
            style={{ color: 'var(--t-text)', margin: 0 }}
          >
            {template.name}
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
            <span
              style={{
                fontSize: 9,
                fontWeight: 600,
                padding: '1px 6px',
                borderRadius: 9999,
                background: 'rgba(99,102,241,0.15)',
                color: 'var(--brand-400)',
              }}
            >
              {template.category}
            </span>
            <span className="text-[10px]" style={{ color: 'var(--t-muted)' }}>
              {pageCount} page{pageCount !== 1 ? 's' : ''}
            </span>
            <span className="text-[10px]" style={{ color: 'var(--t-muted)' }}>
              {createdDate}
            </span>
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        <button
          onClick={() => onUse(template)}
          style={{
            flex: 1,
            padding: '4px 8px',
            borderRadius: 5,
            border: 'none',
            background: 'var(--brand-primary)',
            color: '#fff',
            fontSize: 11,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Use Template
        </button>
        {onDelete && (
          <button
            onClick={() => onDelete(template)}
            style={{
              padding: '4px 8px',
              borderRadius: 5,
              border: '1px solid var(--t-border)',
              background: 'transparent',
              color: '#ef4444',
              fontSize: 11,
              cursor: 'pointer',
            }}
          >
            Delete
          </button>
        )}
      </div>
    </div>
  );
}
